(() => {
  'use strict';

  if (window.__rpcCustomizationGateLoaded === '2.1.0') return;
  window.__rpcCustomizationGateLoaded = '2.1.0';
  window.__rpcCustomizationGateVersion = '2.1.0';
  document.documentElement.dataset.rpcCustomizationGateVersion = '2.1.0';

  const CUSTOM_OPTION_SELECTOR =
    'variant-picker[data-product-id] input[type="radio"][data-option-name="Customization"][value="Add Customizable Back +$5"]';
  // These products are sold as design-required on every variant. Keep this
  // list independent of xTool so a delayed or blocked customizer fails closed.
  const REQUIRED_CUSTOM_PRODUCT_IDS = new Set([
    '9276479308002', // Customizable Backpack
    '9276475277538', // Customizable Aprons
    '9275684618466', // Double Sided Customizable Tank Top
    '9261845872866', // Double Sided Customizable T-Shirt
    '9261839155426' // Double Sided Customizable 19L Zippered Tote Bags
  ]);
  const FORM_COMPONENT_SELECTOR = 'product-form-component[data-product-id]';
  const FORM_SELECTOR = 'form[data-type="add-to-cart-form"]';
  const MAIN_ADD_SELECTOR =
    '[ref="addToCartButton"][name="add"], button[name="add"][data-testid="standalone-add-to-cart"]';
  const ACCELERATED_CHECKOUT_SELECTOR =
    '[ref="acceleratedCheckoutButtonContainer"], .accelerated-checkout-block, .shopify-payment-button[data-shopify="payment-button"]';
  const STICKY_SELECTOR = 'sticky-add-to-cart[data-product-id]';
  const STICKY_ADD_SELECTOR = '[ref="addToCartButton"].sticky-add-to-cart__button';
  const XTOOL_SELECTOR =
    '.xtool-product-info-widget[data-block-id] .xtool-info-button[data-product-id]';
  const GATED_ATTRIBUTE = 'data-rpc-customization-gated';
  const pendingVariantChanges = new Map();

  const getProductId = (element) =>
    element?.closest?.('[data-product-id]')?.dataset.productId || '';

  const getGatedProductIds = () => {
    const ids = new Set(REQUIRED_CUSTOM_PRODUCT_IDS);
    pendingVariantChanges.forEach((_token, productId) => ids.add(productId));
    document.querySelectorAll(CUSTOM_OPTION_SELECTOR).forEach((input) => {
      const productId = getProductId(input);
      if (!productId) return;

      // Gate immediately when the shopper selects customization. When they
      // switch back, Horizon updates the checked radio before it finishes its
      // async variant fetch, so keep the old custom variant gated until the
      // hidden form id has actually changed. This closes that transition race.
      const customVariantId = input.dataset.variantId || '';
      const formVariantId =
        getProductForm(productId, input)?.querySelector('input[name="id"]')?.value || '';
      if (input.checked || (customVariantId && customVariantId === formVariantId)) {
        ids.add(productId);
      }
    });
    return ids;
  };

  const isProductGated = (productId) =>
    Boolean(productId && getGatedProductIds().has(productId));

  const isVisible = (element) =>
    Boolean(element && element.getClientRects().length > 0);

  const getXtoolButton = (productId, origin) => {
    const localRoot = origin?.closest?.(
      'quick-add-dialog, dialog, [data-product-grid-content], .shopify-section'
    );
    const roots = localRoot ? [localRoot, document] : [document];

    for (const root of roots) {
      const matches = [...root.querySelectorAll(XTOOL_SELECTOR)].filter(
        (button) => button.dataset.productId === productId
      );
      const match = matches.find(isVisible);
      if (match) return match;
    }

    return null;
  };

  const getSelectedCustomInput = (productId) =>
    [...document.querySelectorAll(`${CUSTOM_OPTION_SELECTOR}:checked`)].find(
      (input) => getProductId(input) === productId
    ) || null;

  const getProductForm = (productId, origin) => {
    const localRoot = origin?.closest?.(
      'quick-add-dialog, dialog, [data-product-grid-content], .shopify-section'
    );
    const roots = localRoot ? [localRoot, document] : [document];

    for (const root of roots) {
      const components = [...root.querySelectorAll(FORM_COMPONENT_SELECTOR)].filter(
        (component) => component.dataset.productId === productId
      );
      const component = components.find(isVisible) || components[0];
      const form = component?.querySelector(FORM_SELECTOR);
      if (form) return form;
    }

    return null;
  };

  const isDesignerReady = (productId, origin) => {
    const button = getXtoolButton(productId, origin);
    if (!button || !isVisible(button) || button.disabled) return false;
    if (button.dataset.isBound !== 'true' || button.dataset.available === 'false') {
      return false;
    }
    if (
      button.hasAttribute('data-product-mode') ||
      !button.classList.contains('xtool-info-button--active')
    ) {
      return false;
    }

    // A bound, active xTool button is sufficient for products whose every
    // variant requires a design. Products with an optional custom back still
    // require an exact match between the checked option and native form.
    if (REQUIRED_CUSTOM_PRODUCT_IDS.has(String(productId))) return true;

    const selectedVariantId =
      getSelectedCustomInput(productId)?.dataset.variantId || '';
    const formVariantId =
      getProductForm(productId, origin)?.querySelector('input[name="id"]')?.value || '';
    return Boolean(selectedVariantId && selectedVariantId === formVariantId);
  };

  const setDisabled = (button, disabled) => {
    if (button && button.disabled !== disabled) button.disabled = disabled;
  };

  const gateMainAddButton = (button, gated) => {
    if (!button) return;

    if (gated) {
      if (button.dataset.rpcGated !== '1') {
        button.dataset.rpcWasDisabled = String(button.disabled);
      }
      button.dataset.rpcGated = '1';
      setDisabled(button, true);
      button.setAttribute('aria-disabled', 'true');
      return;
    }

    if (button.dataset.rpcGated === '1') {
      setDisabled(button, button.dataset.rpcWasDisabled === 'true');
      delete button.dataset.rpcGated;
      delete button.dataset.rpcWasDisabled;
      button.removeAttribute('aria-disabled');
    }
  };

  const updateStickyButton = (button, gated, ready) => {
    if (!button) return;

    const label = button.querySelector(
      '.add-to-cart-text__content > span:first-child'
    );

    if (gated) {
      if (button.dataset.rpcGated !== '1') {
        button.dataset.rpcWasDisabled = String(button.disabled);
        button.dataset.rpcOriginalAria = button.getAttribute('aria-label') || '';
        if (label) label.dataset.rpcOriginalLabel = label.textContent.trim();
      }

      button.dataset.rpcGated = '1';
      const nextLabel = ready ? 'Use Customize above' : 'Loading designer…';
      if (label && label.textContent.trim() !== nextLabel) label.textContent = nextLabel;
      if (button.getAttribute('aria-label') !== nextLabel) {
        button.setAttribute('aria-label', nextLabel);
      }
      setDisabled(button, true);
      return;
    }

    if (button.dataset.rpcGated === '1') {
      if (label?.dataset.rpcOriginalLabel) {
        label.textContent = label.dataset.rpcOriginalLabel;
        delete label.dataset.rpcOriginalLabel;
      }

      const originalAria = button.dataset.rpcOriginalAria || '';
      if (originalAria) button.setAttribute('aria-label', originalAria);
      else button.removeAttribute('aria-label');

      setDisabled(button, button.dataset.rpcWasDisabled === 'true');
      delete button.dataset.rpcGated;
      delete button.dataset.rpcWasDisabled;
      delete button.dataset.rpcOriginalAria;
    }
  };

  const ensureMessage = (button, gated, ready) => {
    const widget = button?.closest('.xtool-product-info-widget');
    if (
      !widget ||
      (!isVisible(widget) && !widget.nextElementSibling?.matches?.('[data-rpc-gate-message]'))
    ) {
      return;
    }

    let message = widget.nextElementSibling;
    if (!message?.matches?.('[data-rpc-gate-message]')) {
      message = document.createElement('p');
      message.setAttribute('data-rpc-gate-message', '');
      message.className = 'rpc-customization-gate__message';
      widget.insertAdjacentElement('afterend', message);
    }

    message.hidden = !gated;
    const nextMessage = ready
      ? 'Use Customize to design and add this custom-backed item to your cart.'
      : 'Loading the designer…';
    if (message.textContent !== nextMessage) message.textContent = nextMessage;
  };

  const ensureQuickFallback = (component, gated, ready) => {
    const dialog = component.closest('quick-add-dialog, dialog');
    if (!dialog) return;

    const productId = component.dataset.productId || '';
    let link = dialog.querySelector(
      `[data-rpc-quick-fallback="${productId}"]`
    );

    if (!gated || ready) {
      if (link) link.hidden = true;
      return;
    }

    if (!link) {
      link = document.createElement('a');
      link.className = 'button rpc-customization-gate__quick-fallback';
      link.setAttribute('data-rpc-quick-fallback', productId);
      link.textContent = 'Continue to designer';
      component.insertAdjacentElement('afterend', link);
    }

    const form = component.querySelector(FORM_SELECTOR);
    const productUrl = component.dataset.productUrl || form?.dataset.productUrl;
    const variantId = form?.querySelector('input[name="id"]')?.value;
    if (!productUrl) {
      link.hidden = true;
      return;
    }

    const url = new URL(productUrl, window.location.origin);
    if (variantId) url.searchParams.set('variant', variantId);
    link.href = url.toString();
    link.hidden = false;
  };

  const applyGate = () => {
    const gatedProductIds = getGatedProductIds();

    document.querySelectorAll(FORM_COMPONENT_SELECTOR).forEach((component) => {
      const productId = component.dataset.productId || '';
      const gated = gatedProductIds.has(productId);
      component.toggleAttribute(GATED_ATTRIBUTE, gated);
      component.querySelectorAll(MAIN_ADD_SELECTOR).forEach((button) =>
        gateMainAddButton(button, gated)
      );
      ensureQuickFallback(
        component,
        gated,
        gated && isDesignerReady(productId, component)
      );
    });

    document.querySelectorAll(STICKY_SELECTOR).forEach((sticky) => {
      const productId = sticky.dataset.productId || '';
      const gated = gatedProductIds.has(productId);
      const ready = gated && isDesignerReady(productId, sticky);
      sticky.toggleAttribute(GATED_ATTRIBUTE, gated);
      sticky.querySelectorAll(STICKY_ADD_SELECTOR).forEach((button) =>
        updateStickyButton(button, gated, ready)
      );
    });

    document.querySelectorAll(XTOOL_SELECTOR).forEach((button) => {
      const productId = button.dataset.productId || '';
      const gated = gatedProductIds.has(productId);
      ensureMessage(button, gated, gated && isDesignerReady(productId, button));
    });
  };

  const openDesigner = (productId, origin) => {
    const button = getXtoolButton(productId, origin);
    if (button) {
      button.scrollIntoView({ behavior: 'smooth', block: 'center' });
      button.focus({ preventScroll: true });
      const target = button.closest('.xtool-product-info-widget') || button;
      target.setAttribute('data-rpc-targeted', '');
      window.setTimeout(() => {
        target.removeAttribute('data-rpc-targeted');
      }, 1800);
      return;
    }

    const quickDialog = origin?.closest?.('quick-add-dialog, dialog');
    if (quickDialog) {
      const component =
        origin.closest?.(FORM_COMPONENT_SELECTOR) ||
        [...quickDialog.querySelectorAll(FORM_COMPONENT_SELECTOR)].find(
          (candidate) => candidate.dataset.productId === productId
        );
      const form = component?.querySelector(FORM_SELECTOR);
      const productUrl = component?.dataset.productUrl || form?.dataset.productUrl;
      const variantId = form?.querySelector('input[name="id"]')?.value;

      if (productUrl) {
        const url = new URL(productUrl, window.location.origin);
        if (variantId) url.searchParams.set('variant', variantId);
        window.location.assign(url.toString());
        return;
      }
    }

    button?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const stopNativePurchase = (event, productId, origin) => {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    openDesigner(productId, origin);
  };

  const queueGateUpdates = () => {
    [0, 100, 300, 800, 1600].forEach((delay) => {
      window.setTimeout(applyGate, delay);
    });
  };

  const injectStyles = () => {
    if (document.getElementById('rpc-customization-gate-styles')) return;
    const style = document.createElement('style');
    style.id = 'rpc-customization-gate-styles';
    const requiredFormGateSelectors = [...REQUIRED_CUSTOM_PRODUCT_IDS].flatMap(
      (productId) => {
        const selector =
          `product-form-component[data-product-id="${productId}"]`;
        return [
          `${selector} add-to-cart-component`,
          `${selector} [ref="acceleratedCheckoutButtonContainer"]`,
          `${selector} .accelerated-checkout-block`,
          `${selector} .shopify-payment-button[data-shopify="payment-button"]`
        ];
      }
    );
    const requiredStickyGateSelectors = [...REQUIRED_CUSTOM_PRODUCT_IDS].map(
      (productId) =>
        `sticky-add-to-cart[data-product-id="${productId}"] .sticky-add-to-cart__button`
    );
    style.textContent = [
      // Fail closed as soon as this first-party asset evaluates, before xTool
      // or the MutationObserver is ready.
      `${requiredFormGateSelectors.join(',\n')} {`,
      '  display: none !important;',
      '}',
      `${requiredStickyGateSelectors.join(',\n')} {`,
      '  pointer-events: none !important;',
      '}',
      `product-form-component[${GATED_ATTRIBUTE}] add-to-cart-component,`,
      `product-form-component[${GATED_ATTRIBUTE}] [ref="acceleratedCheckoutButtonContainer"],`,
      `product-form-component[${GATED_ATTRIBUTE}] .accelerated-checkout-block,`,
      `product-form-component[${GATED_ATTRIBUTE}] .shopify-payment-button[data-shopify="payment-button"] {`,
      '  display: none !important;',
      '}',
      '.rpc-customization-gate__message {',
      '  margin: 0.5rem 0 0;',
      '  color: inherit;',
      '  font-size: 0.875rem;',
      '  line-height: 1.4;',
      '  opacity: 0.72;',
      '}',
      '.rpc-customization-gate__message[hidden] { display: none !important; }',
      '.rpc-customization-gate__quick-fallback {',
      '  box-sizing: border-box;',
      '  margin-top: 0.75rem;',
      '  text-align: center;',
      '  width: 100%;',
      '}',
      '.rpc-customization-gate__quick-fallback[hidden] { display: none !important; }',
      '.xtool-product-info-widget[data-rpc-targeted] .xtool-info-button,',
      '.xtool-info-button[data-rpc-targeted] {',
      '  outline: 2px solid currentColor;',
      '  outline-offset: 3px;',
      '}',
      '@media (max-width: 749px) {',
      `  sticky-add-to-cart[${GATED_ATTRIBUTE}] .add-to-cart-text,`,
      `  sticky-add-to-cart[${GATED_ATTRIBUTE}] .add-to-cart-text__content {`,
      '    display: flex !important;',
      '  }',
      `  sticky-add-to-cart[${GATED_ATTRIBUTE}] .sticky-add-to-cart__button {`,
      '    width: auto !important;',
      '    min-width: 8.5rem;',
      '    padding-inline: 1rem;',
      '  }',
      '}'
    ].join('\n');
    document.head.appendChild(style);
  };

  const init = () => {
    document.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;

        const stickyButton = event.target.closest(
          `${STICKY_SELECTOR} ${STICKY_ADD_SELECTOR}`
        );
        const formComponent = event.target.closest(FORM_COMPONENT_SELECTOR);
        const nativePurchaseControl = event.target.closest(
          `${MAIN_ADD_SELECTOR}, ${ACCELERATED_CHECKOUT_SELECTOR}`
        );
        const context = stickyButton?.closest(STICKY_SELECTOR) || formComponent;
        const productId = context?.dataset.productId || '';

        if (!productId || !isProductGated(productId)) return;
        if (!stickyButton && !nativePurchaseControl) return;
        stopNativePurchase(event, productId, context);
      },
      true
    );

    document.addEventListener(
      'submit',
      (event) => {
        if (!(event.target instanceof Element) || !event.target.matches(FORM_SELECTOR)) {
          return;
        }

        const productId = getProductId(event.target);
        if (!isProductGated(productId)) return;
        stopNativePurchase(event, productId, event.target);
      },
      true
    );

    document.addEventListener(
      'change',
      (event) => {
        if (
          event.target instanceof Element &&
          event.target.matches('input[data-option-name="Customization"]')
        ) {
          queueGateUpdates();
        }
      },
      true
    );

    document.addEventListener('shopify:product:select', (event) => {
      const promise = event.promise || event.detail?.promise;
      const productId =
        event.target instanceof Element ? getProductId(event.target) : '';

      if (promise && typeof promise.then === 'function') {
        const token = Symbol('rpc-variant-change');
        if (productId) pendingVariantChanges.set(productId, token);
        applyGate();

        promise.then(
          () => {
            // Let Horizon's own promise handlers morph the form and hidden
            // variant id before this temporary lock can be released.
            window.setTimeout(() => {
              if (productId && pendingVariantChanges.get(productId) === token) {
                pendingVariantChanges.delete(productId);
              }
              queueGateUpdates();
            }, 0);
          },
          () => {
            // A failed variant refresh leaves the purchase state uncertain.
            // Retain the lock until the shopper makes a successful selection.
            applyGate();
          }
        );
      } else {
        queueGateUpdates();
      }
    });

    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(() => {
        queued = false;
        applyGate();
      });
    });

    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: [
        'class',
        'disabled',
        'hidden',
        'open',
        'style',
        'data-is-bound',
        'data-available',
        'data-current-variant-id',
        'data-current-checked'
      ]
    });

    window.addEventListener('pageshow', queueGateUpdates);
    window.addEventListener('popstate', queueGateUpdates);
    document.addEventListener('shopify:section:load', queueGateUpdates);
    queueGateUpdates();
  };

  if (document.readyState === 'loading') {
    injectStyles();
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    injectStyles();
    init();
  }
})();
